/* =============================================================================
 * M.M.LaFleur — gallery de-duplication.
 *
 * The repeats are in the catalogue, not the template. Products carry the same
 * photograph attached several times over. Port Wine, for example, has eleven
 * media for six distinct shots: every image appears once with a Shopify
 * dedupe suffix and once without.
 *
 *   ...PortWine_SizeS_OF_Front_1_b536904f-e96b-48f5-96ed-364ced970845.jpg
 *   ...PortWine_SizeS_OF_Front_1.jpg
 *
 * Shopify appends that UUID when a file of the same name is uploaded a second
 * time, so this is the signature of an import that ran twice. Others carry the
 * same frame at two rendition sizes as separate media — Sky Blue has both
 * _2500x2500 and _600x900 copies of four shots.
 *
 * The right long-term fix is cleaning the product media in admin. This is the
 * theme-side guard so the galleries read correctly in the meantime, and it
 * stays harmless once the data is tidied.
 *
 * Runs deferred, before lazy images below the fold have fetched, so the
 * duplicates are usually removed without ever being downloaded.
 *
 * ALT TEXT (v4.7.9, ADA). After de-duplicating, each PDP gallery is
 * renumbered so screen readers hear a distinct label per frame:
 *   - images whose alt the theme generated (data-mm-alt-auto, see
 *     snippets/mm_product_media.liquid) become "<Name>, <Colour>, image N of M",
 *     counted over the frames that are left;
 *   - a catalogue alt used on more than one remaining frame gets
 *     ", image N of M" appended, so no two frames read the same;
 *   - the callout tile (data-mm-callout) is not a photo and is not counted.
 * The zoom viewer's "Zoom <alt>" label is refreshed to match.
 * ========================================================================== */

(function () {
  "use strict";

  var UUID = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  var EXT = /\.(jpe?g|png|webp|avif|gif)$/i;
  var SIZE = /[-_]\d{2,4}x\d{2,4}/gi;

  /* Reduce a CDN URL to the underlying photograph, ignoring the accidents of
     how it was uploaded: path, cache-busting query, file extension, Shopify's
     duplicate-upload UUID, and any baked-in rendition size. */
  function keyFor(src) {
    if (!src) return "";
    var file = src.split("?")[0].split("/").pop();
    file = file.replace(EXT, "");
    file = file.replace(UUID, "");
    file = file.replace(SIZE, "");
    /* Trailing separators left behind by the strips above. */
    file = file.replace(/[-_.]+$/, "");
    return file.toLowerCase();
  }

  function sourceOf(img) {
    if (!img) return "";
    var set = img.getAttribute("srcset");
    if (set) {
      var first = set.split(",")[0].trim().split(/\s+/)[0];
      if (first) return first;
    }
    return img.getAttribute("src") || img.currentSrc || "";
  }

  function dedupe(container, itemSelector) {
    var items = container.querySelectorAll(itemSelector);
    if (items.length < 2) return 0;

    var seen = Object.create(null);
    var removed = 0;

    Array.prototype.forEach.call(items, function (item) {
      /* Video tiles have no comparable source and are never duplicated in the
         way stills are — leave them alone. */
      if (item.querySelector("video")) return;
      /* The press callout tile is not a photograph. */
      if (item.hasAttribute("data-mm-callout")) return;

      var key = keyFor(sourceOf(item.querySelector("img")));
      if (!key) return;

      if (seen[key]) {
        item.parentNode.removeChild(item);
        removed += 1;
      } else {
        seen[key] = true;
      }
    });

    return removed;
  }

  /* Distinct alt text per remaining frame. Idempotent: the catalogue alt is
     kept in data-mm-alt-orig, so a re-run rebuilds from it rather than
     appending a second "image N of M". */
  function relabel(gallery) {
    var frames = Array.prototype.filter.call(
      gallery.querySelectorAll(":scope > .mm-gallery-item"),
      function (f) { return !f.hasAttribute("data-mm-callout"); }
    );
    var total = frames.length;
    if (!total) return;

    var seen = Object.create(null);
    frames.forEach(function (f) {
      var img = f.querySelector(":scope > img");
      if (!img || img.hasAttribute("data-mm-alt-auto")) return;
      if (!img.hasAttribute("data-mm-alt-orig")) {
        img.setAttribute("data-mm-alt-orig", img.getAttribute("alt") || "");
      }
      var own = img.getAttribute("data-mm-alt-orig").trim().toLowerCase();
      if (own) seen[own] = (seen[own] || 0) + 1;
    });

    frames.forEach(function (f, i) {
      var n = ", image " + (i + 1) + " of " + total;
      var label = null;
      var img = f.querySelector(":scope > img");
      if (img) {
        var base = img.getAttribute("data-mm-alt-auto");
        if (base !== null) {
          label = base + n;
        } else {
          var own = img.getAttribute("data-mm-alt-orig") || "";
          label = seen[own.trim().toLowerCase()] > 1 ? own + n : own;
        }
        if (img.getAttribute("alt") !== label) img.setAttribute("alt", label);
        if (img.hasAttribute("data-mm-zoomable")) {
          var zoom = "Zoom " + (label || "product image");
          if (img.getAttribute("aria-label") !== zoom) img.setAttribute("aria-label", zoom);
        }
      }
    });
  }

  function run() {
    /* PDP gallery. */
    Array.prototype.forEach.call(
      document.querySelectorAll(".mm-gallery"),
      function (g) { dedupe(g, ".mm-gallery-item"); relabel(g); }
    );

    /* Product card hover carousel. Duplicates here are worse than cosmetic:
       the card only takes the first few media, so a repeat consumes a slot
       that a genuinely different shot would have filled. */
    Array.prototype.forEach.call(
      document.querySelectorAll("[data-mm-card-media]"),
      function (c) {
        var removed = dedupe(c, "[data-mm-card-slide]");
        if (!removed) return;
        /* The arrows are only meaningful with more than one frame left. */
        var frame = c.closest(".mm-card-frame") || c;
        var arrows = frame.querySelector(".mm-card-arrows");
        if (arrows && c.querySelectorAll("[data-mm-card-slide]").length < 2) {
          arrows.style.display = "none";
        }
      }
    );
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }

  /* Barba swaps the main container, bringing fresh galleries with it. */
  var pending = null;
  new MutationObserver(function () {
    if (pending) window.clearTimeout(pending);
    pending = window.setTimeout(run, 60);
  }).observe(document.body, { childList: true, subtree: true });
}());
